import {
  Geofence,
  useCreateGeofenceMutation,
  useDeleteGeofenceMutation,
  useUpdateGeofenceMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseGeofenceMutationProps = {}

export type UseGeofenceMutationResult = {
  createGeofence: (geofence: Partial<Geofence>, callback: EntityMutationCallback<Geofence> | undefined) => void
  updateGeofence: (geofence: Partial<Geofence>, callback: EntityMutationCallback<Geofence> | undefined) => void
  deleteGeofence: (geofenceId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useGeofenceMutation = (props: UseGeofenceMutationProps = {}): UseGeofenceMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createGeofence, {isLoading: isCreating}] = useCreateGeofenceMutation()
  const [_updateGeofence, {isLoading: isUpdating}] = useUpdateGeofenceMutation()
  const [_deleteGeofence, {isLoading: isDeleting}] = useDeleteGeofenceMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useGeofenceUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.geofence", "Geofence"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useGeofenceUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.geofence", "Geofence"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useGeofenceUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.geofence", "Geofence"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Geofence>, callback: EntityMutationCallback<Geofence> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofence", "Geofence") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<Geofence>, callback: EntityMutationCallback<Geofence> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofence", "Geofence") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofence", "Geofence") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((geofenceId: string, callback: EntityMutationCallback<Geofence> | undefined) => {
    const promise = _deleteGeofence({id: geofenceId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteGeofence, handleDeleteResult])

  const createGeofence = useCallback((geofence: Partial<Geofence>, callback: EntityMutationCallback<Geofence> | undefined) => {
    const promise = _createGeofence(geofence).unwrap()
    handleCreateResult(promise, callback)
  }, [_createGeofence, handleCreateResult])

  const updateGeofence = useCallback((geofence: Partial<Geofence>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateGeofence(geofence).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateGeofence, handleUpdateResult])

  const deleteGeofence = useCallback((geofenceId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.geofence", "Geofence"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.geofence.confirm", "Are you sure you want to delete this geofence?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(geofenceId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createGeofence: createGeofence,
    updateGeofence: updateGeofence,
    deleteGeofence: deleteGeofence,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
